import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import HRHeader from "../components/HRHeader";
import api from "../api";
import { toast } from "sonner";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { BadgeCheck, Download, FileText, Plus } from "lucide-react";

const CERT_TYPES = {
  servizio: "Attestato di servizio",
  reddito: "Certificazione di reddito",
  presenza: "Attestato di presenza",
};

const fmt = (iso) => { try { return format(new Date(iso), "dd MMM yyyy 'ore' HH:mm", { locale: it }); } catch { return iso; } };

const downloadCert = async (id, label) => {
  try {
    const res = await api.get(`/certificates/${id}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${label || "certificato"}.pdf`; document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch { toast.error("Errore nel download"); }
};

const CertificatiPage = () => {
  const [list, setList] = useState([]);
  const [type, setType] = useState("servizio");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get("/certificates/mine").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const request = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/certificates/request", { type });
      toast.success("Certificato generato! Scaricalo dalla lista.");
      load();
      setTimeout(() => downloadCert(res.data.id, res.data.label), 300);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#f0f2f5]" data-testid="certificati-page">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2"><BadgeCheck size={24} /> Certificati e Attestati</h1>
        <p className="text-[14px] text-gray-500 mb-6">Richiedi e scarica attestati ufficiali in formato PDF.</p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-5 sticky top-32">
              <h2 className="text-[16px] font-bold text-[#16345c] mb-4 flex items-center gap-2"><Plus size={18} /> Nuova richiesta</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-[12px] font-semibold text-gray-600 mb-1">Tipo di certificato</label>
                  <Select value={type} onValueChange={setType}>
                    <SelectTrigger className="w-full" data-testid="cert-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(CERT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <p className="text-[12.5px] text-gray-500 leading-relaxed">Il certificato viene generato automaticamente con i tuoi dati e un numero di protocollo univoco.</p>
                <button onClick={request} disabled={submitting} data-testid="cert-request" className="w-full bg-[#16345c] hover:bg-[#0d1b2f] text-white font-bold text-[14px] py-3 rounded-sm uppercase tracking-wide transition-colors disabled:opacity-60">
                  {submitting ? "Generazione..." : "Genera certificato"}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h2 className="text-[16px] font-bold text-[#16345c] mb-4">I miei certificati</h2>
            {list.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-400">
                <FileText size={40} className="mx-auto mb-3 opacity-40" /> Nessun certificato richiesto
              </div>
            ) : (
              <div className="space-y-3" data-testid="cert-list">
                {list.map((c) => (
                  <div key={c.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-10 h-10 rounded-lg bg-[#0096401a] text-[#009640] flex items-center justify-center shrink-0"><BadgeCheck size={20} /></span>
                      <div className="min-w-0">
                        <div className="text-[14px] font-bold text-[#16345c] truncate">{c.label}</div>
                        <div className="text-[12px] text-gray-500">Prot. {c.protocol} · {fmt(c.created_at)}</div>
                      </div>
                    </div>
                    <button onClick={() => downloadCert(c.id, c.label)} data-testid={`cert-download-${c.id}`} className="shrink-0 flex items-center gap-1 bg-[#16345c] hover:bg-[#0d1b2f] text-white text-[12px] font-bold px-3 py-1.5 rounded-sm transition-colors"><Download size={14} /> Scarica PDF</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default CertificatiPage;
